// 3rd party
import { Component, OnInit, OnDestroy, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs/Rx';
import { TranslateService } from '@ngx-translate/core';
import { MatSidenav } from '@angular/material';
import * as FileSaver from 'file-saver';
import { PdfJsViewerComponent } from 'ng2-pdfjs-viewer';

// components
import { AppComponent } from 'app/app.component';
import { LayoutMainComponent } from 'app/shared/layout/layout-main/layout-main.component';

// helpers
import { Helper } from 'app/shared/helpers/helper';

// models
import { ECalibrationStatus, EApproveCalibration, Calibration } from 'app/calibrations/shared/calibration.model';
import { InstrumentBand } from 'app/instruments/shared/instrument-band.model';
import { Instrument } from 'app/instruments/shared/instrument.model';
import { ERoleActionWebview, ContactCustomer } from 'app/shared/models/contact-customer.model';
import { StoredFile } from 'app/shared/models/stored-file.model';
import { EReferenceModeType } from 'app/shared/models/cal-criterion.model';

// services
import { InstrumentService } from 'app/instruments/shared/instrument.service';
import { DialogsService } from 'app/shared/services/dialog/dialog.service';
import { StoredFileService } from 'app/shared/services/stored-file.service';
import { CriterionService } from 'app/shared/services/criterion.service';
import { CalibrationService } from 'app/calibrations/shared/calibration.service';

@Component({
  selector: 'app-calibration-list',
  templateUrl: './calibration-list.component.html',
  styleUrls: ['./calibration-list.component.css']
})
export class CalibrationListComponent implements OnInit, OnDestroy {

  @ViewChild('sidenavRight') sidenavRight: MatSidenav;
  @ViewChild('pdfViewer') pdfViewer: PdfJsViewerComponent;

  CalibrationStatus: typeof ECalibrationStatus = ECalibrationStatus;
  cancelSidenavOpen: boolean = false;
  checkedItems: number = 0;
  checkedMenuItems: string[] = [];
  chkAll: boolean;
  columns: any[] = [
    { tag: 's', name: 'Status', value: 'Translate.CustomerCriterionEvaluation', isChecked: true, isIconColumn: true, tooltipValue: 'Translate.CustomerCriterionEvaluation' },
    { tag: 'ad', name: 'CustomerStatus', value: 'Translate.CustomerEvaluation', isChecked: true, isIconColumn: true, tooltipValue: 'Translate.CustomerEvaluation' },
    { tag: 'att', name: 'HasAttachment', value: 'Translate.HasAttachments', isChecked: true, isIconColumn: true, tooltipValue: 'Translate.HasAttachments' },
    { tag: 'c', name: 'Code', value: 'Translate.Code', isChecked: true },
    { tag: 'd', name: 'Date', value: 'Translate.Date', isChecked: true },
    { tag: 'p', name: 'IsField', value: 'Translate.Local', isChecked: true },
    { tag: 't', name: 'CalibrationType.Name', value: 'Translate.Type', isChecked: true },
    { tag: 'ico', name: 'Instrument.Code', value: 'Translate.Instrument', isChecked: true },
    { tag: 'isn', name: 'Instrument.SerialNumber', value: 'Translate.SerialNumber', isChecked: false },
    { tag: 'iba', name: 'InstrumentBand', value: 'Translate.Bands', isChecked: false },
    { tag: 'it', name: 'Instrument.InstrumentType.Name', value: 'Translate.InstrumentType', isChecked: true },
    { tag: 'sn', name: 'Instrument.Supplier.Name', value: 'Translate.Supplier', isChecked: false },
    { tag: 'os', name: 'OrderService.Code', value: 'Translate.OrderService', isChecked: false },
    { tag: 'cs', name: 'Instrument.CustomerSector.Name', value: 'Translate.Sector', isChecked: false },
    { tag: 'icc', name: 'IdCalibrationCorrected', isChecked: false }
  ];
  CustomerStatus: typeof EApproveCalibration = EApproveCalibration;
  currentPage: number = 1;
  data: any[] = [];
  filter: string;
  hasAttachmentPermission: boolean = false;
  hasCertificatesDownloadPermission: boolean = false;
  hasCertificatesViewCriterionAcceptance: boolean = false;
  hasCertificatesViewPermission: boolean = false;
  hasChangeStatusPermission: boolean = false;
  hasSupplementPermission: boolean = false;
  instrumentsBands: InstrumentBand[] = [];
  isCheckingAll: boolean = false;
  isLoading: boolean = false;
  isSearching: boolean = false;
  months: any[];
  orderBy: string = 'code';
  overallCount: number = 1;
  pageSize: number = 25;
  qtVisibleColumns: number = this.columns.filter(c => c.isChecked).length;
  ReferenceModeType: typeof EReferenceModeType = EReferenceModeType;
  search: string = '';
  selectedItems: any = [];
  selectedMonth = new Date().getMonth() + 1;
  selectedSituation = -1;
  selectedYear = new Date().getFullYear();
  situations: any[];
  sortOrder: string = 'asc';
  subscribe: Subscription;
  totalPages: number = 1;
  years: any[];

  constructor(
    private app: AppComponent,
    private calibrationService: CalibrationService,
    public criterionService: CriterionService,
    public dialog: DialogsService,
    private el: ElementRef,
    private instrumentService: InstrumentService,
    public mainLayout: LayoutMainComponent,
    private renderer2: Renderer2,
    private router: Router,
    public storedFileService: StoredFileService,
    public translate: TranslateService
  ) { }

  ngOnInit() {
    let columnTag = localStorage.getItem('newCalibrationTagName');
    if (columnTag) {
      this.columns.push({ tag: 'tg', name: 'Tag', value: columnTag, isChecked: false });
    }
    this.checkPermissions();
    this.refresh();
    this.fillFilters();
    this.checkColumnsPreferences();
  }

  ngOnDestroy() {
    this.subscribe.unsubscribe();
  }

  checkAll(ev) {
    this.isCheckingAll = true;
    this.data.forEach((item) => {
      item.checked = ev.source.checked;
    });
    this.isCheckingAll = false;
    this.checkedItems = this.data.filter(item => item.checked).length;
  }

  checkColumn(event, str: string) {
    event.stopPropagation();

    let idx = this.columns.findIndex(c => c.tag === str);
    if (idx === -1) {
      return;
    }

    if (this.columns.filter(c => c.isChecked).length === 1 && this.columns[idx].isChecked) {
      return;
    }

    this.columns[idx].isChecked = !this.columns[idx].isChecked;
    this.qtVisibleColumns = this.columns.filter(c => c.isChecked).length;

    this.saveColumnsPreferences(this.columns);
  }

  checkColumnsPreferences() {
    if (!localStorage.getItem('webview.calibrationList.columns')) {
      this.saveColumnsPreferences(this.columns);
    }
    const savedColumns = JSON.parse(localStorage.getItem('webview.calibrationList.columns'));

    this.columns.forEach(c => c.isChecked = savedColumns.findIndex(sc => sc === c.tag) !== -1);
    this.qtVisibleColumns = this.columns.filter(c => c.isChecked).length;
  }

  checkPermissions() {
    const contact = JSON.parse(localStorage.getItem('currentContact')) as ContactCustomer;
    this.hasChangeStatusPermission = ContactCustomer.hasPermission(ERoleActionWebview.CertificatesChangeStatus, contact.RoleWebView.ActionsPermissions);
    this.hasCertificatesDownloadPermission = ContactCustomer.hasPermission(ERoleActionWebview.CertificatesDownload, contact.RoleWebView.ActionsPermissions);
    this.hasCertificatesViewPermission = ContactCustomer.hasPermission(ERoleActionWebview.CertificatesView, contact.RoleWebView.ActionsPermissions);
    this.hasAttachmentPermission = ContactCustomer.hasPermission(ERoleActionWebview.CertificatesViewAttachments, contact.RoleWebView.ActionsPermissions);
    this.hasCertificatesViewCriterionAcceptance = ContactCustomer.hasPermission(ERoleActionWebview.CertificatesViewCriterionAcceptance, contact.RoleWebView.ActionsPermissions);
    this.hasSupplementPermission = ContactCustomer.hasPermission(ERoleActionWebview.CertificatesViewSupplements, contact.RoleWebView.ActionsPermissions);
  }

  closeSidenav() {
    this.sidenavRight.close();
    this.router.navigate([`/${this.app.currentContact.Domain}/calibrations/`]);
  }

  downloadCal() {
    this.isLoading = true;
    let selectedCalibrations = this.data.filter(d => d.checked) as Calibration[];
    let params = selectedCalibrations.map(calibration => {
      return {
        identifier: calibration.Identifier,
        tableName: calibration.TableName
      };
    });

    this.calibrationService.download(params)
      .subscribe(data => {
        this.isLoading = false;
        if (data) {
          FileSaver.saveAs(data, selectedCalibrations.length > 1 ? 'certificados.cal' : `${selectedCalibrations[0].Code}.cal`);
        } else {
          this.dialog.error('Message.ErrorAlert', this.checkedItems > 1 ? 'Message.DownloadCertificatesError' : 'Message.DownloadCertificateError');
        }
      }, () => {
        this.isLoading = false;
        this.dialog.error('Message.ErrorAlert', this.checkedItems > 1 ? 'Message.DownloadCertificatesError' : 'Message.DownloadCertificateError');
      });
  }

  enableSearch(ev) {
    this.isSearching = !this.isSearching;
    this.search = '';
  }

  fillFilters() {
    this.fillSituationFilter();
    this.fillMonthFilter();
    this.fillYearFilter();
  }

  fillMonthFilter() {
    this.months = new Array(
      { id: -1, name: 'Configurations.FilterAll' },
      { id: 1, name: 'Months.January' },
      { id: 2, name: 'Months.February' },
      { id: 3, name: 'Months.March' },
      { id: 4, name: 'Months.April' },
      { id: 5, name: 'Months.May' },
      { id: 6, name: 'Months.June' },
      { id: 7, name: 'Months.July' },
      { id: 8, name: 'Months.August' },
      { id: 9, name: 'Months.September' },
      { id: 10, name: 'Months.October' },
      { id: 11, name: 'Months.November' },
      { id: 12, name: 'Months.December' },
    );
    this.selectedMonth = new Date().getMonth() + 1;
  }

  fillSituationFilter() {
    this.situations = new Array(
      { id: -1, name: 'Configurations.FilterAll' },
      { id: 0, name: 'Translate.None' },
      { id: 2, name: 'Translate.Disapproved' },
      { id: 1, name: 'Translate.Approved' },
      { id: 3, name: 'Translate.ApprovedWithReservation' },
    );
    this.selectedSituation = -1;
  }

  fillYearFilter() {
    this.years = new Array({ id: -1, name: 'Configurations.FilterAll' });
    let currentYear = new Date().getFullYear();
    for (let i = 2013; i <= currentYear; i++) {
      this.years.push({ id: i, name: i.toString() });
    }
    this.selectedYear = currentYear;
  }

  filterValueChanged() {
    this.correctSearch(true, false);
    this.refresh(this.search);
  }

  getSituationDescription(status: number) {
    return this.calibrationService.getSituationDescription(status);
  }

  getSituationIcon(status: number) {
    return this.calibrationService.getSituationIcon(status);
  }

  getStatusIcon(status: number) {
    return this.calibrationService.getStatusIcon(status);
  }

  isColumnChecked(str: string): boolean {
    let idx = this.columns.findIndex(c => c.tag === str);
    if (idx === -1) {
      return false;
    }

    return this.columns[idx].isChecked;
  }

  nextPage(e) {
    this.correctSearch(false, false);
    this.currentPage = e;
    this.refresh();
  }

  onSelectionChange(a) {
    if (!this.isCheckingAll) {
      if (this.data.filter(item => item.checked).length !== this.data.length && this.chkAll) {
        this.chkAll = false;
      }
      if (this.data.filter(item => item.checked).length === this.data.length && !this.chkAll) {
        this.chkAll = true;
      }

      this.selectedItems = this.data ? this.data.filter(d => d.checked) : [];
      this.checkedItems = this.selectedItems.length;
    }
  }

  openCustomerStatusDialog(readOnly: boolean) {
    let lst: any[] = [];
    lst = this.data.filter(d => d.checked);
    this.dialog.customerStatus(lst, readOnly).subscribe((res) => {
      if (res === undefined && !readOnly) {
        this.refresh(this.search, undefined, undefined);
      }
    });
  }

  openDetails(item: any) {
    if (this.cancelSidenavOpen) {
      this.cancelSidenavOpen = false;
      return;
    }

    this.router.navigate([`/${this.app.currentContact.Domain}/calibrations/` + item.Identifier]);
    this.sidenavRight.open();
  }

  prevent($event) {
    this.cancelSidenavOpen = true;
    $event.preventDefault();
  }

  previousPage(e) {
    this.correctSearch(false, false);
    this.currentPage = e;
    this.refresh();
  }

  correctSearch(setCurrentPage: boolean, setCleanSearch: boolean) {
    this.currentPage = setCurrentPage ? 1  : this.currentPage;
    this.search = setCleanSearch ? null : this.search;
  }

  refresh(search?: string, orderBy?: string, sortOrder?: string) {
    this.filter = '';
    this.selectedItems = this.data ? this.data.filter(d => d.checked) : [];
    if (search !== undefined) {
      this.search = search;
    }
    if (orderBy) {
      this.orderBy = orderBy;
    }
    if (sortOrder) {
      this.sortOrder = sortOrder;
    }
    let y = new Date().getFullYear();
    let dInitial = new Date(2013, 0, 1);
    let dFinal = new Date(y, 11, 31);
    if (this.selectedMonth !== undefined && this.selectedMonth !== -1 && !this.mainLayout.isMobile) {
      dInitial.setMonth(this.selectedMonth - 1);
      dFinal = new Date(y, this.selectedMonth, 0);
      this.filter += (this.filter ? ',' : '') + '"Date_BETWEEN \'' + dInitial.toISOString().split('T')[0] + '\' AND \'' + dFinal.toISOString().split('T')[0] + '\'"';
    }
    if (this.selectedYear !== undefined && this.selectedYear !== -1 && !this.mainLayout.isMobile) {
      dInitial.setFullYear(this.selectedYear);
      dFinal.setFullYear(this.selectedYear);
      this.filter += (this.filter ? ',' : '') + '"Date_BETWEEN \'' + dInitial.toISOString().split('T')[0] + '\' AND \'' + dFinal.toISOString().split('T')[0] + '\'"';
    }
    if (this.selectedSituation !== undefined && this.selectedSituation !== -1 && !this.mainLayout.isMobile) {
      this.filter += (this.filter ? ',' : '') + '"CustomerStatus_=' + this.selectedSituation + '"';
    }

    // permissão de visualização por setor
    if (!this.app.currentContact.ViewAllCustomerSectorsWebView) {
      if (this.app.currentContact.CustomerSectorWebView && this.app.currentContact.CustomerSectorWebView.Identifier) {
        this.filter += (this.filter ? ', ' : '') + '"Instrument.CustomerSector.Identifier_=\'' + this.app.currentContact.CustomerSectorWebView.Identifier + '\'"';
      } else {
        this.filter += (this.filter ? ', ' : '') + '"Instrument.CustomerSector.Identifier_is null"';
      }
    }
    if (this.app.currentContact !== undefined && this.app.currentContact.Customer !== undefined) {
      this.filter += (this.filter ? ', ' : '') + '"Customer.Identifier_=\'' + this.app.currentContact.Customer.Identifier + '\'"';
    } else {
      this.filter += (this.filter ? ', ' : '') + '"Customer.Identifier_=\'ERROR\'"';
    }
    this.filter += (this.filter ? ', ' : '') + '"Status_=1"';
    const lstSpinner = this.el.nativeElement.querySelectorAll('mat-spinner');
    for (let x = 0; x < lstSpinner.length; x++) {
      this.renderer2.setStyle(lstSpinner[x], 'display', 'block');
    }
    this.subscribe = this.calibrationService.get(null, this.search, this.orderBy, this.sortOrder, this.pageSize, this.currentPage, this.filter, null).subscribe(data => {
      for (let x = 0; x < lstSpinner.length; x++) {
        this.renderer2.setStyle(lstSpinner[x], 'display', 'none');
      }

      this.data = Helper.deserialize(data.text());

      if (this.data.length > 0) {
        const instruments = this.data.map(c => c.Instrument);
        this.instrumentService.getbands(instruments as Instrument[]).subscribe();
      }

      if (this.selectedItems) {
        this.data.forEach(d => d.checked = this.selectedItems.find(s => s.Identifier === d.Identifier && s.checked) ? true : false);
      }
      this.checkedItems = this.data.filter(d => d.checked).length;
      this.overallCount = this.data.length > 0 ? this.data[0].OverallCount : 0;
      this.totalPages = Helper.getTotalPages(this.pageSize, this.overallCount);
    });
  }

  saveColumnsPreferences(columns: any[]) {
    localStorage.setItem('webview.calibrationList.columns', JSON.stringify(columns.filter(c => c.isChecked).map(c2 => c2.tag)));
  }

  searchData() {
    this.correctSearch(true, false);
    this.refresh(this.search);
  }

  setCustomerStatus() {
    this.openCustomerStatusDialog(false);
  }

  setPageSize(n) {
    this.pageSize = n;
    this.currentPage = 1;
    this.refresh();
  }

  sortData(data) {
    this.refresh(undefined, data.orderBy, data.sortOrder);
  }

  view(download: boolean) {
    this.isLoading = true;
    let identifiers = this.data.filter(d => d.checked).map(obj => obj.Identifier);
    let lstFilters = '"Calibration.Identifier_in(\'' + identifiers.join('\',\'') + '\')"';
    let lstFields = '"SystemFileName","Calibration.Id","Calibration.Code","Calibration.Date"';
    let storedFiles: any[] = [];

    this.storedFileService.get(null, null, null, null, null, null, lstFilters, lstFields)
      .subscribe(value => {
        storedFiles = value.json() as StoredFile[];
        if (!storedFiles || storedFiles.length === 0) {
          this.dialog.error('Message.ErrorAlert', 'Message.ViewCertificateError', 'info_outline', '#009688');
          this.isLoading = false;
          return;
        }
        this.storedFileService.download(storedFiles)
          .subscribe(data => {
            this.isLoading = false;
            if (data) {
              const file = new Blob([data], { type: data.type });

              this.storedFileService.view(file, download, this.pdfViewer, `Certificado ${storedFiles[0].Calibration.Code}`);

            } else {
              this.dialog.error('Message.ErrorAlert', 'Message.ViewCertificateError');
              this.isLoading = false;
            }
          }, () => {
            this.dialog.error('Message.ErrorAlert', 'Message.OpenCertificateError');
            this.isLoading = false;
          });
      }, () => {
        this.dialog.error('Message.ErrorAlert', 'Message.OpenCertificateError');
        this.isLoading = false;
      });
  }

  viewCustomerStatus() {
    this.openCustomerStatusDialog(true);
  }
}



// WEBPACK FOOTER //
// ./src/app/calibrations/calibration-list/calibration-list.component.ts